import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Button, Card, Alert, Container, Row, Col, InputGroup, Spinner } from 'react-bootstrap';
import { Eye, EyeSlash } from 'react-bootstrap-icons';
import AuthContext from '../../context/AuthContext';
import LandingPage from './LandingPage';
import './Login.css';
import './LandingPage.css';

const Login = () => {
  const [showLoginForm, setShowLoginForm] = useState(false);
  const [loginType, setLoginType] = useState('staff');
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [rememberMe, setRememberMe] = useState(false);
  const [validated, setValidated] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loginError, setLoginError] = useState(null);
  
  const { username, password } = formData;
  const { login, user, error, setError } = useContext(AuthContext);
  const navigate = useNavigate();
  
  // Load saved username on component mount
  useEffect(() => {
    const savedUsername = localStorage.getItem('rememberedUsername');
    const savedLoginType = localStorage.getItem('rememberedLoginType');
    
    if (savedUsername) {
      setFormData(prev => ({ ...prev, username: savedUsername }));
      setRememberMe(true);
      if (savedLoginType) {
        setLoginType(savedLoginType);
      }
    }
  }, []);
  
  useEffect(() => {
    if (user) {
      navigate('/');
    }
    
    return () => {
      if (setError) setError(null);
    };
  }, [user, navigate, setError]);
  
  const onChange = e => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setLoginError(null);
  };
  
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };
  
  const onSubmit = async e => {
    e.preventDefault();
    
    const form = e.currentTarget;
    if (form.checkValidity() === false) {
      e.stopPropagation();
      setValidated(true);
      return;
    }
    
    setIsSubmitting(true);
    setLoginError(null);
    
    try {
      const success = await login(username, password, loginType);
      if (success) {
        // Handle Remember Me
        if (rememberMe) {
          localStorage.setItem('rememberedUsername', username);
          localStorage.setItem('rememberedLoginType', loginType);
        } else {
          localStorage.removeItem('rememberedUsername');
          localStorage.removeItem('rememberedLoginType');
        }
        
        navigate('/');
      } else {
        setLoginError('Login failed. Please check your credentials.');
      }
    } catch (err) {
      console.error('Login error:', err);
      setLoginError('Server error. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  if (!showLoginForm) {
    return <LandingPage 
      onStaffLoginClick={() => { setLoginType('staff'); setShowLoginForm(true); }}
      onPatientLoginClick={() => { setLoginType('patient'); setShowLoginForm(true); }}
    />;
  }

  return (
    <div className="login-wrapper">
      <div className="login-container">
        <div className="login-left-panel">
          <div className="medical-icon-container">
            <div className="icon-box icon-1">
              <i className="fas fa-heartbeat"></i>
            </div>
            <div className="icon-box icon-2">
              <i className="fas fa-user-md"></i>
            </div>
            <div className="icon-box icon-3">
              <i className="fas fa-stethoscope"></i>
            </div>
          </div>
          <h1 className="system-title">Medical Management System</h1>
          <p className="system-subtitle">Empowering Healthcare Through Technology</p>
          <div className="feature-icons">
            <div className="feature-icon-item">
              <i className="fas fa-users"></i>
            </div>
            <div className="feature-icon-item">
              <i className="fas fa-file-medical"></i>
            </div>
            <div className="feature-icon-item">
              <i className="fas fa-laptop-medical"></i>
            </div>
          </div>
        </div>
        
        <div className="login-right-panel">
          <div className="login-form-container">
            <button className="back-to-website" onClick={() => setShowLoginForm(false)}>
              <i className="fas fa-arrow-left"></i> Back to Website
            </button>
            
            <div className="login-type-tabs">
              <button 
                className={`login-tab ${loginType === 'staff' ? 'active' : ''}`}
                onClick={() => setLoginType('staff')}
              >
                <i className="fas fa-user-tie"></i> Staff Login
              </button>
              <button 
                className={`login-tab ${loginType === 'patient' ? 'active' : ''}`}
                onClick={() => setLoginType('patient')}
              >
                <i className="fas fa-user"></i> Patient Login
              </button>
            </div>
            
            <h2 className="welcome-title">{loginType === 'staff' ? 'Staff Portal' : 'Patient Portal'}</h2>
            <p className="welcome-subtitle">{loginType === 'staff' ? 'Login to access dashboard' : 'View your appointments & records'}</p>
            
            {(error || loginError) && (
              <Alert variant="danger" className="login-alert">{error || loginError}</Alert>
            )}
            
            <Form noValidate validated={validated} onSubmit={onSubmit} className="login-form">
              <Form.Group className="mb-4" controlId="username">
                <div className="input-with-icon">
                  <i className="fas fa-user input-icon"></i>
                  <Form.Control
                    type="text"
                    name="username"
                    value={username}
                    onChange={onChange}
                    placeholder="Username"
                    required
                    disabled={isSubmitting}
                    className="custom-input"
                  />
                </div>
                <Form.Control.Feedback type="invalid">
                  Please provide a valid username.
                </Form.Control.Feedback>
              </Form.Group>
              
              <Form.Group className="mb-4" controlId="password">
                <div className="input-with-icon">
                  <i className="fas fa-lock input-icon"></i>
                  <Form.Control
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={password}
                    onChange={onChange}
                    placeholder="Password"
                    required
                    disabled={isSubmitting}
                    className="custom-input"
                  />
                  <button 
                    type="button"
                    className="password-toggle"
                    onClick={togglePasswordVisibility}
                    disabled={isSubmitting}
                  >
                    {showPassword ? <EyeSlash /> : <Eye />}
                  </button>
                </div>
                <Form.Control.Feedback type="invalid">
                  Please provide your password.
                </Form.Control.Feedback>
              </Form.Group>
              
              <Form.Group className="mb-4">
                <Form.Check 
                  type="checkbox" 
                  label="Remember me" 
                  className="remember-checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
              </Form.Group>
              
              <Button 
                type="submit" 
                disabled={isSubmitting}
                className="login-button"
              >
                {isSubmitting ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    Logging in...
                  </>
                ) : (
                  <>
                    <i className="fas fa-sign-in-alt me-2"></i>
                    Login to Dashboard
                  </>
                )}
              </Button>
            </Form>
            
            <div className="designer-info">
              <i className="fas fa-info-circle"></i>
              <span>Developer: Abdullah Arif / +92-3137323866</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login; 